/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description Keycloak authentication hook
 */
import { useContext } from 'react';
import { HookAPI } from 'antd/es/modal/useModal';
import Keycloak, { KeycloakInitOptions, KeycloakLoginOptions } from 'keycloak-js';

import { MODAL_TYPES } from 'components/widgets/modal';
import { IKeycloakContext, KeycloakContext } from './index';
import { getLogoutOptions } from 'utils/router';

interface LogoutProps {
	keycloak: Keycloak;
}

interface ILogoutWithPromptProps extends LogoutProps {
	callback?: VoidFunction;
	modal: HookAPI;
}

interface UseKeycloakResults {
	keycloak: Keycloak;
	isAuthenticated: Keycloak['authenticated'];
	initOptions: KeycloakInitOptions;
	loginOptions: KeycloakLoginOptions;
}

/**
 * Logout and clear storage
 */
const logoutAndClearStorage = async ({ keycloak }: LogoutProps): Promise<void> => {
	localStorage.clear();
	sessionStorage.clear();
	return keycloak?.logout?.(getLogoutOptions());
};

/**
 * Logout with prompt
 */
const logoutWithPrompt = ({ keycloak, modal, callback }: ILogoutWithPromptProps): void => {
	modal.confirm({
		...MODAL_TYPES.confirm,
		content: 'Are you sure you want to logout ?',
		okText: 'Log out',
		onOk: async () => {
			localStorage.clear();
			sessionStorage.clear();
			if (typeof callback === 'function') {
				callback();
			}
			return await keycloak?.logout?.(getLogoutOptions());
		},
	});
};

const useKeycloak = (): UseKeycloakResults => {
	const { keycloak, initOptions, loginOptions } = useContext<IKeycloakContext>(KeycloakContext);
	return { keycloak, isAuthenticated: keycloak.authenticated, initOptions, loginOptions };
};

export { logoutWithPrompt, logoutAndClearStorage };
export default useKeycloak;
